import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';

export enum BulletType {
  PLAYER = 'player',
  ENEMY = 'enemy'
}

export class Bullet implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private type: BulletType;
  private speed: number;
  private color: string;
  private worldWidth: number;
  private lifeTime: number = 0;
  private maxLifeTime: number = 3; // 3 seconds max
  private cameraX: number = 0; // Store camera position for rendering

  constructor(
    id: string,
    x: number,
    y: number,
    type: BulletType,
    direction: number,
    renderManager: RenderManager,
    worldWidth: number,
    speedOverride?: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.size = { x: 4, y: 2 };
    this.renderManager = renderManager;
    this.type = type;
    this.worldWidth = worldWidth;
    
    if (type === BulletType.PLAYER) {
      this.speed = speedOverride || 600; // Fast player bullets
      this.velocity = { x: this.speed * direction, y: 0 };
      this.color = '#FFFFFF'; // White bullets for player
    } else {
      this.speed = speedOverride || 200; // Slower enemy bullets
      this.velocity = { x: this.speed * direction, y: 0 };
      this.color = '#883932'; // Red bullets for enemies
    }
  }

  public update(deltaTime: number): void {
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Update lifetime
    this.lifeTime += deltaTime;
    if (this.lifeTime > this.maxLifeTime) {
      this.active = false;
      return;
    }
    
    // World wraparound for bullets (authentic Defender mechanic)
    if (this.position.x < -this.size.x) {
      this.position.x = this.worldWidth;
    } else if (this.position.x > this.worldWidth) {
      this.position.x = -this.size.x;
    }
    
    // Deactivate if off screen vertically
    if (this.position.y < -10 || this.position.y > 610) {
      this.active = false;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    // Calculate screen position
    const screenX = this.position.x - this.cameraX;
    const screenY = this.position.y;
    
    // Don't render if off screen
    if (screenX < -10 || screenX > 810) return;

    if (this.type === BulletType.PLAYER) {
      // Player bullets are simple rectangles
      this.renderManager.fillRect(
        screenX,
        screenY,
        this.size.x,
        this.size.y,
        this.color
      );
    } else {
      // Enemy bullets are more visible
      this.renderManager.fillRect(
        screenX,
        screenY,
        this.size.x,
        this.size.y,
        this.color
      );
      
      // Add a glow effect for enemy bullets
      this.renderManager.fillRect(
        screenX - 1,
        screenY - 1,
        this.size.x + 2,
        this.size.y + 2,
        'rgba(136, 57, 50, 0.3)'
      );
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public getType(): BulletType {
    return this.type;
  }

  public destroy(): void {
    this.active = false;
  }

  public getVelocity(): Vector2 {
    return { ...this.velocity };
  }

  public setCameraX(cameraX: number): void {
    this.cameraX = cameraX;
  }
}